import { type ReactNode } from "react";
import {
  Control, Controller, FieldPath, FieldValues,
} from "react-hook-form";
import { Label, Select } from "flowbite-react";
import { P } from "@/shared/ui/typographies/paragraph";

type SelectOption = {
  readonly value: string
  readonly label: string
};

type ControlledSelect<T extends FieldValues> = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> & {
  id: string
  label?: ReactNode
  control: Control<T>
  name: FieldPath<T>
  options: readonly SelectOption[]
  dataTestId?: string
  wrapperClassName?: string
  color?: "failure" | "success" | "base"
  helperText?: string
  disabled?: boolean
};

export const ControlledSelect = <T extends FieldValues>({
  id,
  label,
  control,
  name,
  options,
  dataTestId,
  wrapperClassName,
  color,
  helperText,
  disabled,
  ...props
}: ControlledSelect<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const labelColor = color === "failure" || error ? "failure" : "default";
        return (
          <div className={`w-full ${wrapperClassName || ""}`}>
            {label && <Label htmlFor={id} color={labelColor} className="!text-foreground">{label}</Label>}
            <Select
              id={id}
              data-testid={dataTestId}
              data-slot="select"
              className="mt-2 form-control focus:outline-none! focus:shadow-none! focus:ring-0!"
              style={{ borderColor: color === "failure" || error ? "red" : "" }}
              {...props}
              {...field}
              disabled={disabled}
            >
              {options.map((option) => (
                <option className="flex items-center gap-2" key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {error
              ? (
                <P className="mt-1 text-sm break-words hyphens-auto max-w-full" color="error">
                  {error.message}
                </P>
              )
              : helperText
                ? (
                  <P className="mt-1 text-sm break-words hyphens-auto max-w-full text-red-600 dark:text-red-500">
                    {helperText}
                  </P>
                )
                : null}
          </div>
        );
      }}
    />
  );
};
